import { FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import { formatCpfCnpj, formatDate, formatCurrency } from '../../../utils/collateralFormatters';
import useVehiclePageList from '../../../hooks/useVehiclePageList';

const columns = [
    { id: 'plate', title: 'Placa', field: 'plate', canSort: true, width: '90px' },
    { id: 'vehicleType', title: 'Tipo', field: 'vehicleType', canSort: false, width: '100px', cssClass: 'text-center' },
    { id: 'brand', title: 'Marca', field: 'brand', canSort: true, width: '110px' },
    { id: 'model', title: 'Modelo', field: 'model', canSort: true, width: '180px' },
    { id: 'yearModel', title: 'Ano', field: 'yearModel', canSort: true, width: '80px', cssClass: 'text-center' },
    { id: 'owner', title: 'Proprietário', field: 'owner', canSort: true, width: '220px' },
    { id: 'createdAt', title: 'Cadastro', field: 'createdAt', canSort: true, width: '90px', cssClass: 'text-center' },
];

const VehicleAssetPageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        vehicleType, setVehicleType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedVehicle, vehicleDetail, isLoadingDetail, closePanel,
        apiInfo,
    } = useVehiclePageList();

    const typeCounts = filterCounts?.vehicleType || {};
    const totalAll = Object.values(typeCounts).reduce((a: number, b: any) => a + (b?.count || 0), 0);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => { setVehicleType(''); setArchived('false'); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!vehicleType
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{totalAll}</span>
                        </div>
                    </button>

                    {Object.keys(typeCounts).length > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">TIPO</div>
                            <div className="space-y-1">
                                {Object.entries(typeCounts).map(([key, data]: [string, any]) => (
                                    <button key={key} onClick={() => setVehicleType(key)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${vehicleType === key
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                        <div className="flex items-center justify-between">
                                            <span>{data.label || key}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{data.count}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">SITUAÇÃO</div>
                        <div className="space-y-1">
                            <button onClick={() => setArchived('false')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'false'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Ativos
                            </button>
                            <button onClick={() => setArchived('true')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'true'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Arquivados
                            </button>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const renderRightPanel = () => {
        if (isLoadingDetail) {
            return <div className="p-4 text-gray-500 dark:text-gray-400">Carregando...</div>;
        }
        if (!vehicleDetail) {
            return <div className="p-4 text-gray-500 dark:text-gray-400">Selecione um veículo</div>;
        }

        const d = vehicleDetail;
        return (
            <div className="p-4 space-y-4 overflow-auto h-full">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{d.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{d.plateFormatted} • {d.yearFormatted}</p>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dados do Veículo</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500 dark:text-gray-400">Marca:</span> <span className="dark:text-white">{d.brand}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Modelo:</span> <span className="dark:text-white">{d.model}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Chassi:</span> <span className="dark:text-white font-mono text-xs">{d.chassis}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Renavam:</span> <span className="dark:text-white">{d.renavam}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Cor:</span> <span className="dark:text-white">{d.color || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">UF:</span> <span className="dark:text-white">{d.plateState}</span></div>
                    </div>
                </div>

                {d.owner && (
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Proprietário</h4>
                        <div className="text-sm dark:text-white">{d.owner.name}</div>
                        {d.owner.document && <div className="text-xs text-gray-500 dark:text-gray-400">{formatCpfCnpj(d.owner.document)}</div>}
                    </div>
                )}

                {d.allocations && d.allocations.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Obrigações</h4>
                        {d.allocations.map((alloc: any) => (
                            <div key={alloc.creditOperationId} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-2 text-sm">
                                <div className="dark:text-white font-medium">{alloc.creditOperationId}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {alloc.legalRegime} • {alloc.status} • {alloc.allocatedAmount ? formatCurrency(alloc.allocatedAmount) : '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {d.costs && d.costs.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Custos / Registros</h4>
                        {d.costs.map((c: any) => (
                            <div key={c.id} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="dark:text-white font-medium">{c.costTypeFormatted}</span>
                                    <span className="dark:text-white">{formatCurrency(c.value)}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {c.costDate && formatDate(c.costDate)}
                                    {c.registrationNumber && ` • Nº ${c.registrationNumber}`}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {d.comments && (
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Comentários</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{d.comments}</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={!!selectedVehicle}
                onCloseRightPanel={closePanel}
                rightPanel={renderRightPanel()}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Veículos</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <button onClick={() => navigate('/collateral/vehicles/new')}
                                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                    <FaPlus size={12} /> Novo
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar placa, chassi, marca, proprietário..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum veículo encontrado</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} ${selectedVehicle?.id === row.id ? 'bg-blue-50 dark:bg-slate-700' : ''}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-mono font-semibold text-blue-700 dark:text-blue-400">{row.plateFormatted}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                                {row.vehicleTypeFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.brand}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.model}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 text-center">{row.yearFormatted}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">
                                            {row.ownerName ? (
                                                <>
                                                    <div className="truncate" title={row.ownerName}>{row.ownerName}</div>
                                                    {row.ownerDocument && <div className="text-xs text-gray-400">{formatCpfCnpj(row.ownerDocument)}</div>}
                                                </>
                                            ) : null}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 text-center">{formatDate(row.createdAt)}</td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ refresh }} />
        </>
    );
};

export default VehicleAssetPageList;
